import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

import { TranslationFn } from "lib/types";
import FollowUp from "common/Question/FollowUp";
import SelectOneAutoselect from "common/Question/SelectOneAutoselect";
import SelectOneRadio from "common/Question/SelectOneRadio";
import { fromNow } from "lib/date";
import { useDefaultToast } from "lib/hooks/toasts";
import { useIsBreakpoint } from "lib/hooks/window";
import { QuestionAnswerShape, QuestionComponentType, QuestionProps } from "./types";

const SelectOne: QuestionComponentType = (props: QuestionProps, { t: _ }: { t: TranslationFn }) => {
    const {
        answer,
        id,
        autoSelect,
        question,
        isRequired,
        handleAnswer,
        handleFollowUpStart,
        toggleAnonymous,
        isAnonymous,
        handleSkip,
        response,
        submit_response: submitResponse,
        isLastQuestion,
        CustomSelectComponent,
        question_set_question, // eslint-disable-line camelcase
        image_url: imageUrl,
    } = props;

    type SelectedAnswer = {
        selected: QuestionAnswerShape;
        answerData: {
            questionID: string | number;
            response: number | string;
            responseText: string;
            question_set_question: number;
        };
    };

    const [selectedAnswer, setSelectedAnswer] = useState<null | SelectedAnswer>(null);
    const [followUpOptional, setFollowUpOptional] = useState(false);
    const [showFollowUp, setShowFollowUp] = useState(false);
    const { breakpointXS } = useIsBreakpoint();

    const toast = useDefaultToast();

    // localResponse is used to display a pre-selected response value, may be either a pre-recorded
    // answer (response.response) or a "soft" response value which isn't persisted (response)
    const [localResponse, setLocalResponse] = useState<string | undefined>(
        typeof response === "string" ? response : response?.response,
    );

    function getSelectedAnswer(answerId: number | string) {
        return answer.filter((a) => a.id === answerId || a.id === Number(answerId)).shift();
    }

    function handleSelect(answerId: number | string) {
        const selected = getSelectedAnswer(answerId);

        if (!selected) {
            return { submitted: false };
        }

        const answerData = {
            questionID: id,
            response: selected.id,
            responseText: selected.value,
            question_set_question, // eslint-disable-line camelcase
        };

        if (selected.has_follow_up) {
            setSelectedAnswer({
                selected,
                answerData,
            });
            setShowFollowUp(true);
            const optional = selected.follow_up_optional ?? true;
            setFollowUpOptional(optional);

            if (optional && handleFollowUpStart) {
                handleFollowUpStart(answerData);
            }

            return { submitted: false };
        }

        setShowFollowUp(false);
        setFollowUpOptional(false);

        handleAnswer(answerData);

        return { submitted: true };
    }

    const onSelect: React.ChangeEventHandler<HTMLInputElement> = (event) => {
        // When an explicit selection is made ensure localResponse is only populated if there's a
        // pre-recorded answer; if the response wasn't saved in the db then this clears it
        setLocalResponse(response?.response);

        handleSelect(event.currentTarget.dataset.id || "");
    };

    const handleSelfIdentify = (val: boolean) => {
        if (toggleAnonymous != null) {
            toggleAnonymous(!val);
        }
    };

    function onFollowUp(followUp: string) {
        if (!followUp && !followUpOptional) {
            return;
        }
        if (!selectedAnswer) {
            return;
        }

        handleAnswer({
            ...selectedAnswer.answerData,
            followUp,
            selfIdentified: !isAnonymous,
            question_set_question, // eslint-disable-line camelcase
        });
        handleSelfIdentify(false);
    }

    const onDeselect: React.MouseEventHandler<HTMLButtonElement> = (event) => {
        event.preventDefault();

        setSelectedAnswer(null);
        setShowFollowUp(false);
        handleSelfIdentify(false);
    };

    useEffect(() => {
        if (typeof response === "string") {
            // If we have a response value that isn't already recorded and we're instructed to
            // submit it then trigger the selection handler here; note if there's follow-up we will
            // simply move to that screen
            const selected = getSelectedAnswer(response);
            if (selected && submitResponse) {
                const { submitted } = handleSelect(response);
                const toastMsg = submitted
                    ? _("We recorded your answer of {value}", { value: selected.value })
                    : _("You selected {value}", { value: selected.value });
                const anchorOrigin = breakpointXS ? { horizontal: "left", vertical: "top" } : null;
                toast(toastMsg, { autoHideDuration: 5000, anchorOrigin });
            }
        }
    }, []);

    const showResponseMessage = response?.response && !showFollowUp;
    const DefaultSelectComponent = autoSelect ? SelectOneAutoselect : SelectOneRadio;
    const SelectComponent =
        CustomSelectComponent != null ? CustomSelectComponent : DefaultSelectComponent;

    return (
        <div>
            {/* Hide question text during follow-up for kiosk */}
            {!showFollowUp && <label className="questions__title">{question}</label>}
            {isRequired && !showFollowUp && (
                <div className="questions__subtitle">Answer Required</div>
            )}
            {imageUrl && !showFollowUp && (
                <div className="questions__image-container">
                    <img src={imageUrl} alt="question related" />
                </div>
            )}
            <SelectComponent
                answer={answer}
                id={id}
                handleSelect={onSelect}
                handleSkip={handleSkip}
                selectedAnswer={selectedAnswer}
                response={localResponse}
                showFollowUp={showFollowUp}
                isLastQuestion={isLastQuestion}
                isRequired={isRequired}
            />
            {props.follow_up && (
                <FollowUp
                    {...props}
                    selectedAnswerData={selectedAnswer?.answerData}
                    handleSelfIdentify={handleSelfIdentify}
                    handleSubmit={onFollowUp}
                    handleBack={selectedAnswer && onDeselect}
                    optional={followUpOptional}
                    visible={showFollowUp}
                    isLastQuestion={isLastQuestion}
                    isAnonymous={isAnonymous}
                    toggleAnonymous={toggleAnonymous}
                    questionText={props.follow_up}
                    isRequired={!followUpOptional}
                />
            )}
            {showResponseMessage && response.time_response && (
                <div className="questions__question-response-notice">
                    *Your response {fromNow(response.time_response)}
                </div>
            )}
        </div>
    );
};

SelectOne.contextTypes = {
    t: PropTypes.func,
};

export default SelectOne;
